import { useImageUrl } from 'client/utils/cdn';
import { Recording } from 'server/types';
import { isAudio, isVideo, isVimeo } from 'shared/media';
import { IcoPaperclip } from '@components/icons';
import { Case } from '@components/conditional';
import { VidstackPlayer } from '@course/components/vidstack-player';

interface Props {
  recording: Recording;
  allowDownload: boolean;
  hideCaptions: boolean;
  hideTitle?: boolean;
}

function Video({ recording, hideCaptions, allowDownload }: Props) {
  const videoUrl = useImageUrl(recording.url);
  const poster = useImageUrl(recording.poster);
  const downloadUrl = useImageUrl(recording.downloadUrl);

  if (!videoUrl) {
    return null;
  }

  return (
    <VidstackPlayer
      id={recording.id}
      url={videoUrl}
      poster={poster}
      hasCaptions={recording.hasCaptions}
      captionsURL={recording.captionsURL}
      hideCaptions={hideCaptions}
      downloadUrl={allowDownload ? downloadUrl : undefined}
      type={recording.type}
    />
  );
}

function VimeoEmbed({ recording }: { recording: Recording }) {
  return (
    <div style="padding:56.25% 0 0 0;position:relative;">
      <iframe
        src={recording.url}
        style="position:absolute;top:0;left:0;width:100%;height:100%;"
        frameborder="0"
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}

function ChatFile({ recording }: Props) {
  const url = useImageUrl(recording.url);

  return (
    <a
      target="blank"
      href={url}
      class="bg-gray-100 dark:bg-gray-800 border border-gray-200 p-2 block rounded-sm"
    >
      <IcoPaperclip class="w-4 h-4 mr-2 inline-block align-middle" />
      {recording.name}
    </a>
  );
}

export function RecordingPlayer(props: Props) {
  return (
    <div class="relative">
      {!props.hideTitle && props.recording.title && (
        <span class="text-base font-medium">{props.recording.title}</span>
      )}
      {isVimeo(props.recording.type) ? (
        <VimeoEmbed recording={props.recording} />
      ) : (
        <Case
          when={isVideo(props.recording.type) || isAudio(props.recording.type)}
          fallback={<ChatFile {...props} />}
        >
          <Video {...props} />
        </Case>
      )}
    </div>
  );
}
